import { Box } from "@chakra-ui/react";
import { useField } from "formik";
import { FC, useCallback, useEffect, useMemo } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { MultipleSelect } from "~/src/components/common/Select";

import type {
  PopupImportGraphqlEnumMultiKey,
  PopupImportSelectOption,
} from "../popupImportColumnModel";
import {
  POPUP_IMPORT_ALLOWED_AGE_RANGE,
  POPUP_IMPORT_ALLOWED_ANNUAL_INCOME,
  POPUP_IMPORT_ALLOWED_BROWSER,
  POPUP_IMPORT_ALLOWED_DEVICE,
  POPUP_IMPORT_ALLOWED_GENDER,
  POPUP_IMPORT_ALLOWED_PLATFORM,
  POPUP_IMPORT_SEGMENT_ALL_VALUE,
  isCompleteEnumSelection,
} from "../popupImportGraphqlEnumCells";

const OPTIONS_BY_KEY: Record<
  PopupImportGraphqlEnumMultiKey,
  ReadonlyArray<PopupImportSelectOption>
> = {
  Device: GraphQLEnums.Device,
  Platform: GraphQLEnums.Platform,
  Browser: GraphQLEnums.Browser,
  Gender: GraphQLEnums.Gender,
  AgeRange: GraphQLEnums.AgeRange,
  AnnualIncome: GraphQLEnums.AnnualIncome,
};

const ALLOWED_BY_KEY: Record<
  PopupImportGraphqlEnumMultiKey,
  ReadonlySet<string>
> = {
  Device: POPUP_IMPORT_ALLOWED_DEVICE,
  Platform: POPUP_IMPORT_ALLOWED_PLATFORM,
  Browser: POPUP_IMPORT_ALLOWED_BROWSER,
  Gender: POPUP_IMPORT_ALLOWED_GENDER,
  AgeRange: POPUP_IMPORT_ALLOWED_AGE_RANGE,
  AnnualIncome: POPUP_IMPORT_ALLOWED_ANNUAL_INCOME,
};

const ALL_OPTION: PopupImportSelectOption = {
  label: "全て",
  value: POPUP_IMPORT_SEGMENT_ALL_VALUE,
};

type Option = { label: string; value: string };

function selectOptionsForKey(key: PopupImportGraphqlEnumMultiKey): Option[] {
  return [ALL_OPTION, ...OPTIONS_BY_KEY[key]];
}

type Props = {
  name: string;
  graphqlEnumMultiKey: PopupImportGraphqlEnumMultiKey;
  menuPortalProps: Record<string, unknown>;
  minW?: string;
};

/**
 * Import-only segment filter: first chip is 「全て」 (`POPUP_IMPORT_SEGMENT_ALL_VALUE`);
 * mutually exclusive with real GraphQL enum values. Omitted on API when only sentinel / empty reals.
 */
export const PopupImportSegmentFilterCell: FC<Props> = ({
  name,
  graphqlEnumMultiKey,
  menuPortalProps,
  minW,
}) => {
  const [{ value }, , { setValue }] = useField<string[]>(name);

  const allowed = ALLOWED_BY_KEY[graphqlEnumMultiKey];
  const mergedOptions = useMemo(
    () => selectOptionsForKey(graphqlEnumMultiKey),
    [graphqlEnumMultiKey]
  );

  useEffect(() => {
    const v = value ?? [];
    if (v.length === 0) {
      void setValue([POPUP_IMPORT_SEGMENT_ALL_VALUE]);
      return;
    }
    if (
      !v.includes(POPUP_IMPORT_SEGMENT_ALL_VALUE) &&
      isCompleteEnumSelection(v, allowed)
    ) {
      void setValue([POPUP_IMPORT_SEGMENT_ALL_VALUE]);
    }
  }, [allowed, setValue, value]);

  const selectedOptions: Option[] = useMemo(() => {
    const vals = value ?? [];
    return mergedOptions.filter((o) => vals.includes(o.value));
  }, [mergedOptions, value]);

  const handleMultiChange = useCallback(
    (selected: readonly Option[] | null) => {
      const nextVals = (selected ?? []).map((o) => o.value);
      const hasSentinel = nextVals.includes(POPUP_IMPORT_SEGMENT_ALL_VALUE);
      const real = nextVals.filter((v) => v !== POPUP_IMPORT_SEGMENT_ALL_VALUE);

      if (nextVals.length === 0) {
        void setValue([POPUP_IMPORT_SEGMENT_ALL_VALUE]);
        return;
      }

      if (hasSentinel && real.length > 0) {
        void setValue(real);
        return;
      }

      if (hasSentinel && real.length === 0) {
        void setValue([POPUP_IMPORT_SEGMENT_ALL_VALUE]);
        return;
      }

      if (isCompleteEnumSelection(real, allowed)) {
        void setValue([POPUP_IMPORT_SEGMENT_ALL_VALUE]);
        return;
      }

      void setValue(real);
    },
    [allowed, setValue]
  );

  const handleSelectChange = useCallback(
    (sel: unknown) => {
      handleMultiChange(sel as Option[] | null);
    },
    [handleMultiChange]
  );

  return (
    <Box minW={minW} py="2px">
      <MultipleSelect
        isMulti
        isSearchable={false}
        closeMenuOnSelect={false}
        options={mergedOptions}
        value={selectedOptions}
        onChange={handleSelectChange}
        {...menuPortalProps}
      />
    </Box>
  );
};
